// Sidenav — mirrors the Angular SidenavComponent, gated by usePermissions.
// Permission codes match the Angular *ngxPermissionsOnly strings exactly:
//   ADMIN, TREASURY,
//   screen:monitoring, view:transfersMonitoring, view:paymentsMonitoring, view:transactionsCorrections,
//   screen:ops_operations, screen:ops_userOperations, screen:ops_nostroOperations, screen:ops_manualId.
import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Collapse,
} from '@mui/material';
import {
  AccountBox,
  Group,
  MonetizationOn,
  DesktopWindows,
  Equalizer,
  Person,
  AccountBalance,
  AccountBalanceWallet,
  Face,
  Business,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { NavLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@/auth/usePermissions';

interface NavLinkItemProps {
  to: string;
  icon: React.ReactNode;
  labelKey: string;
}

function NavLinkItem({ to, icon, labelKey }: NavLinkItemProps) {
  const { t } = useTranslation();
  return (
    <ListItemButton
      component={NavLink}
      to={to}
      sx={{
        '&.active': {
          backgroundColor: 'rgba(63, 81, 181, 0.12)',
          fontWeight: 600,
        },
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={t(labelKey, { defaultValue: labelKey })} />
    </ListItemButton>
  );
}

export function Sidenav() {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();
  const location = useLocation();

  const [pricingOpen, setPricingOpen] = useState(
    location.pathname.startsWith('/pricing'),
  );
  const [monitoringOpen, setMonitoringOpen] = useState(
    location.pathname.startsWith('/monitoring'),
  );
  const [opsOpen, setOpsOpen] = useState(
    location.pathname.startsWith('/user-operations') ||
      location.pathname.startsWith('/nostro-operations') ||
      location.pathname.startsWith('/payment-intermediaries'),
  );

  return (
    <Box sx={{ width: 260, py: 2 }}>
      <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
        <img src="/assets/diool_logo.png" alt="Company Logo" style={{ maxHeight: 48 }} />
      </Box>
      <Divider />
      <List
        subheader={<ListSubheader component="div">APPLICATIONS</ListSubheader>}
      >
        {hasAny(['ADMIN']) && (
          <NavLinkItem to="/accounts" icon={<AccountBox />} labelKey="menus.menuAccounts" />
        )}
        {hasAny(['ADMIN']) && (
          <NavLinkItem to="/user-list" icon={<Group />} labelKey="menus.menuUserList" />
        )}

        {hasAny(['TREASURY', 'ADMIN']) && (
          <>
            <ListItemButton onClick={() => setPricingOpen((o) => !o)}>
              <ListItemIcon>
                <MonetizationOn />
              </ListItemIcon>
              <ListItemText primary={t('menus.menuPricing', { defaultValue: 'Pricing' })} />
              {pricingOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={pricingOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 4 }}>
                <NavLinkItem
                  to="/pricing/global"
                  icon={<MonetizationOn />}
                  labelKey="menus.globalPricing"
                />
              </List>
            </Collapse>
          </>
        )}

        {hasAny(['screen:monitoring']) && (
          <>
            <ListItemButton onClick={() => setMonitoringOpen((o) => !o)}>
              <ListItemIcon>
                <DesktopWindows />
              </ListItemIcon>
              <ListItemText primary={t('menus.monitoring', { defaultValue: 'Monitoring' })} />
              {monitoringOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={monitoringOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 4 }}>
                {hasAny(['view:transfersMonitoring']) && (
                  <NavLinkItem
                    to="/monitoring/transfers"
                    icon={<DesktopWindows />}
                    labelKey="menus.monitoring_transfers"
                  />
                )}
                {hasAny(['view:paymentsMonitoring']) && (
                  <NavLinkItem
                    to="/monitoring/payments"
                    icon={<DesktopWindows />}
                    labelKey="menus.monitoring_payments"
                  />
                )}
                {hasAny(['view:transactionsCorrections']) && (
                  <NavLinkItem
                    to="/monitoring/corrections"
                    icon={<DesktopWindows />}
                    labelKey="menus.monitoring_corrections"
                  />
                )}
              </List>
            </Collapse>
          </>
        )}

        {hasAny(['screen:ops_operations', 'ADMIN']) && (
          <>
            <ListItemButton onClick={() => setOpsOpen((o) => !o)}>
              <ListItemIcon>
                <Equalizer />
              </ListItemIcon>
              <ListItemText primary={t('menus.menuOperations', { defaultValue: 'Operations' })} />
              {opsOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={opsOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding sx={{ pl: 4 }}>
                {hasAny(['screen:ops_userOperations']) && (
                  <NavLinkItem
                    to="/user-operations"
                    icon={<Person />}
                    labelKey="menus.menuUserOperations"
                  />
                )}
                {hasAny(['screen:ops_nostroOperations']) && (
                  <NavLinkItem
                    to="/nostro-operations"
                    icon={<AccountBalance />}
                    labelKey="menus.menuNostroOperations"
                  />
                )}
                {hasAny(['screen:ops_operations']) && (
                  <NavLinkItem
                    to="/payment-intermediaries"
                    icon={<AccountBalanceWallet />}
                    labelKey="menus.menuPaymentIntermediaries"
                  />
                )}
              </List>
            </Collapse>
          </>
        )}

        {hasAny(['screen:ops_manualId']) && (
          <NavLinkItem
            to="/manual-id-verification"
            icon={<Face />}
            labelKey="menus.menuManualIdVerification"
          />
        )}
        {hasAny(['ADMIN']) && (
          <NavLinkItem
            to="/manage-businesses"
            icon={<Business />}
            labelKey="menus.beManagement"
          />
        )}
      </List>
    </Box>
  );
}
